import { useState } from "react";
import {
  BookOpen,
  Clock,
  MessageSquareText,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

type DnaPillar = {
  step: string;
  icon: LucideIcon;
  title: string;
  description: string;
  bgImage: string;
  imageAlt: string;
};

const pillars: DnaPillar[] = [
  {
    step: "1",
    icon: MessageSquareText,
    title: "Comportamento e tom de voz",
    description:
      "Defina nome de apresentação, saudação, função e tom de voz, do formal ao energético. Escolha idioma e fuso. A personalidade vale para WhatsApp e Instagram.",
    bgImage: "/landing/dna/comportamento.jpg",
    imageAlt:
      "Tela de configuração de tom de voz, saudação e personalidade do agente para WhatsApp e Instagram",
  },
  {
    step: "2",
    icon: ShieldCheck,
    title: "Regras, roteiro e limites",
    description:
      "Monte perguntas de qualificação uma por vez, instruções prioritárias e regras de negócio. Decida quando transferir para humano, por funil e estágio, e o que o agente não deve prometer sem validação.",
    bgImage: "/landing/dna/regras.jpg",
    imageAlt:
      "Painel de regras de negócio, perguntas de qualificação e transbordo para atendimento humano",
  },
  {
    step: "3",
    icon: BookOpen,
    title: "Conhecimento e oferta",
    description:
      "Centralize biografia, público alvo, diferenciais e objetivo do agente. Associe produtos, catálogo em PDF, pagamento, entrega, endereços e redes. Respostas mais rápidas sem repetir informação.",
    bgImage: "/landing/dna/conhecimento.jpg",
    imageAlt:
      "Base de conhecimento com biografia da empresa, catálogo de produtos e informações de oferta",
  },
  {
    step: "4",
    icon: Clock,
    title: "Horários e automação",
    description:
      "Configure horário por dia e se responde fora do expediente. Ative acompanhamento automático com intervalo e tentativas. Integre Google Calendar para agendar, reagendar e mandar lembretes antes da reunião.",
    bgImage: "/landing/dna/automacao.jpg",
    imageAlt:
      "Configuração de horário de atendimento, acompanhamento automático e integração com agenda",
  },
];

const DEFAULT_ACTIVE = 1;

const EASE_OUT_QUINT = "cubic-bezier(0.22, 1, 0.36, 1)";

type EnterDelay = "none" | "short" | "medium" | "long";

const enterDelayClass: Record<EnterDelay, string> = {
  none: "",
  short: "motion-safe:delay-100",
  medium: "motion-safe:delay-200",
  long: "motion-safe:delay-300",
};

function enterMotion(isInView: boolean, delay: EnterDelay = "none") {
  return cn(
    "motion-safe:transition-[opacity,transform] motion-safe:duration-[650ms] motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)]",
    enterDelayClass[delay],
    !isInView &&
      "translate-y-3 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100",
    isInView && "translate-y-0 opacity-100",
  );
}

function cardEntranceMotion(isInView: boolean, index: number) {
  return cn(
    "motion-safe:transition-[opacity,transform] motion-safe:duration-[600ms] motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)]",
    !isInView &&
      "translate-y-4 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100",
    isInView && "translate-y-0 opacity-100",
  );
}

function DnaAccordionCard({
  pillar,
  index,
  isActive,
  isSectionInView,
  onActivate,
  layout = "horizontal",
}: {
  pillar: DnaPillar;
  index: number;
  isActive: boolean;
  isSectionInView: boolean;
  onActivate: () => void;
  layout?: "horizontal" | "stacked";
}) {
  const Icon = pillar.icon;
  const entranceDelayMs = index * 70;

  return (
    <article
      role="button"
      tabIndex={0}
      aria-expanded={isActive}
      onMouseEnter={onActivate}
      onFocus={onActivate}
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onActivate();
        }
      }}
      className={cn(
        "group relative flex min-w-0 cursor-default flex-col overflow-hidden rounded-xl border bg-card",
        "motion-safe:transition-[flex,box-shadow,border-color,min-height] motion-safe:duration-[450ms]",
        "motion-reduce:transition-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-card",
        cardEntranceMotion(isSectionInView, index),
        layout === "horizontal" && "min-h-[380px]",
        layout === "horizontal" &&
          (isActive
            ? "flex-[2.4_1_0%] border-primary/15 shadow-[0_4px_20px_-8px_hsl(var(--primary)/0.12)]"
            : "flex-[1_1_0%] border-border/70 hover:border-border"),
        layout === "stacked" &&
          (isActive
            ? "min-h-[320px] border-primary/15 shadow-[0_4px_16px_-8px_hsl(var(--primary)/0.1)]"
            : "min-h-[112px] border-border/70 hover:border-border"),
      )}
      style={{
        transitionProperty:
          "opacity, transform, flex, box-shadow, border-color, min-height",
        transitionDuration: "600ms, 600ms, 450ms, 450ms, 450ms, 450ms",
        transitionTimingFunction: EASE_OUT_QUINT,
        transitionDelay: isSectionInView
          ? `${entranceDelayMs}ms, ${entranceDelayMs}ms, 0ms, 0ms, 0ms, 0ms`
          : "0ms",
      }}
    >
      <img
        src={pillar.bgImage}
        alt={pillar.imageAlt}
        loading="lazy"
        decoding="async"
        className={cn(
          "pointer-events-none absolute inset-0 h-full w-full object-cover object-[center_20%]",
          "motion-safe:transition-[opacity,transform] motion-safe:duration-[450ms] motion-reduce:transition-none",
          isActive ? "opacity-[0.52] motion-safe:scale-[1.03]" : "opacity-[0.38] scale-100",
        )}
        style={{ transitionTimingFunction: EASE_OUT_QUINT }}
      />
      <div
        className={cn(
          "pointer-events-none absolute inset-0 bg-gradient-to-t from-background from-[30%] via-background/96 to-background/50",
          "motion-safe:transition-opacity motion-safe:duration-300 motion-reduce:transition-none",
          isActive ? "opacity-100" : "opacity-[0.98]",
        )}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[58%] bg-gradient-to-t from-background via-background/95 to-transparent"
        aria-hidden
      />

      <div className="relative z-[1] mt-auto flex flex-col px-5 pb-5 pt-4 sm:px-6 sm:pb-6">
        <div className="mb-3 flex items-center gap-3">
          <div
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted ring-1 ring-border/70",
              "motion-safe:transition-[background-color,box-shadow,transform] motion-safe:duration-200 motion-reduce:transition-none",
              isActive
                ? "bg-primary/10 ring-primary/15 motion-safe:scale-100"
                : "motion-safe:scale-[0.96] group-hover:motion-safe:scale-100",
            )}
            style={{ transitionTimingFunction: EASE_OUT_QUINT }}
          >
            <Icon
              className={cn(
                "h-4 w-4 text-primary motion-safe:transition-colors motion-safe:duration-200",
              )}
              strokeWidth={1.75}
              aria-hidden
            />
          </div>

          <div
            className={cn(
              "grid min-w-0 flex-1 motion-safe:transition-[grid-template-rows,opacity] motion-safe:duration-300 motion-reduce:transition-none",
              isActive ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
            )}
            style={{
              transitionTimingFunction: EASE_OUT_QUINT,
              transitionDelay: isActive ? "120ms" : "0ms",
            }}
          >
            <span className="overflow-hidden text-xs font-medium tabular-nums text-foreground/65">
              Etapa {pillar.step} de 4
            </span>
          </div>
        </div>

        <h3 className="text-base font-semibold leading-snug tracking-tight text-foreground sm:text-[1.05rem]">
          {pillar.title}
        </h3>

        <div
          className={cn(
            "grid motion-safe:transition-[grid-template-rows,margin-top] motion-safe:duration-[450ms] motion-reduce:transition-none",
            isActive ? "mt-2.5 grid-rows-[1fr]" : "mt-0 grid-rows-[0fr]",
          )}
          style={{ transitionTimingFunction: EASE_OUT_QUINT }}
        >
          <div className="min-h-0 overflow-hidden">
            <p
              className={cn(
                "text-sm leading-relaxed text-foreground/85",
                "motion-safe:transition-[opacity,transform] motion-reduce:transition-none",
                isActive
                  ? "motion-safe:translate-y-0 motion-safe:opacity-100 motion-safe:duration-300 motion-safe:delay-150"
                  : "motion-safe:translate-y-1 motion-safe:opacity-0 motion-safe:duration-150 motion-safe:delay-0",
              )}
              style={{ transitionTimingFunction: EASE_OUT_QUINT }}
            >
              {pillar.description}
            </p>
          </div>
        </div>
      </div>
    </article>
  );
}

export const AgentDNA = () => {
  const [activeIndex, setActiveIndex] = useState(DEFAULT_ACTIVE);
  const { ref, isInView } = useInView({ threshold: 0.1 });

  return (
    <section
      id="dna"
      className="scroll-mt-24 border-t border-border/40 bg-background py-20 md:py-28"
    >
      <div className="container mx-auto max-w-6xl px-4">
        <div ref={ref} className={cn("mb-12 max-w-2xl md:mb-14", enterMotion(isInView))}>
          <p className="mb-3 text-sm font-medium text-foreground/60">DNA do agente</p>

          <h2 className="text-pretty text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-[2.75rem] lg:leading-[1.12]">
            IA com o <span className="text-primary">DNA</span> do seu negócio
          </h2>
          <p className="mt-4 text-base leading-relaxed text-foreground/72 md:text-lg">
            Monte o agente em camadas: como ele fala, o que pode prometer, o que sabe sobre a empresa
            e quando atende. Tudo visual, sem código, no mesmo painel em que sua equipe trabalha.
          </p>
        </div>

        <div
          className="hidden items-stretch gap-2.5 lg:flex"
          onMouseLeave={() => setActiveIndex(DEFAULT_ACTIVE)}
        >
          {pillars.map((pillar, index) => (
            <DnaAccordionCard
              key={pillar.step}
              pillar={pillar}
              index={index}
              isSectionInView={isInView}
              isActive={activeIndex === index}
              onActivate={() => setActiveIndex(index)}
              layout="horizontal"
            />
          ))}
        </div>

        <div className="flex flex-col gap-2.5 lg:hidden">
          {pillars.map((pillar, index) => (
            <DnaAccordionCard
              key={pillar.step}
              pillar={pillar}
              index={index}
              isSectionInView={isInView}
              isActive={activeIndex === index}
              onActivate={() => setActiveIndex(index)}
              layout="stacked"
            />
          ))}
        </div>

        <div className={cn("mt-10 flex justify-start md:mt-12", enterMotion(isInView, "medium"))}>
          <Button
            asChild
            className="h-11 rounded-full px-8 text-sm font-semibold shadow-md shadow-primary/20 motion-safe:transition-transform motion-safe:duration-200 motion-safe:hover:scale-[1.02]"
          >
            <Link to="/planos">Ver planos</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
